import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions'; // o3-portalproj-9c4e7b21
import { auth, functions } from '../firebase/config';
import { listPortalQuotes } from '../utils/portalQuoteReads'; // x7portal-6e1a3d58
import { useNavigate } from 'react-router-dom';
import { Loader2, FileText, ChevronRight } from 'lucide-react';
import QuoteStatusBadge from '../components/common/QuoteStatusBadge';
import { resolveDocTerminology } from '../utils/quoteSchema'; // r1-p3-docterm-pquotes-7e2d9b41

// x7portal-6e1a3d58 — X7 (#29): the list comes from the getPortalQuotes callable, which applies
// the visible-status fence (pdraft-5c8e1a74, now PORTAL_VISIBLE_QUOTE_STATUSES in
// functions/utils/portalQuotes.js) and sends only the list columns. The portal can no longer read
// estimate documents directly.

const PortalQuotes = () => {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orgTermDefault, setOrgTermDefault] = useState('quote'); // r1-p3-docterm-pquotes-7e2d9b41 — org-default term for aggregate labels
    const navigate = useNavigate();

    useEffect(() => {
        const fetchQuotes = async () => {
            const user = auth.currentUser;
            if (!user) return;

            try {
                // x7portal-6e1a3d58 — org + customer come from the session on the server.
                setQuotes(await listPortalQuotes());

                // r1-p3-docterm-pquotes-7e2d9b41 — org-default term (non-blocking).
                // o3-portalproj-9c4e7b21 — was a whole-org-doc read for this ONE
                // field; now the server projection. The truthy guard is kept so an
                // absent value leaves the 'quote' default above untouched.
                try {
                    const getOrgInfo = httpsCallable(functions, 'getPublicOrganizationInfo');
                    const orgResult = await getOrgInfo();
                    if (orgResult.data?.quoteTerminologyDefault) {
                        setOrgTermDefault(orgResult.data.quoteTerminologyDefault);
                    }
                } catch (termErr) {
                    console.warn('PortalQuotes: could not load org terminology default', termErr);
                }
            } catch (error) {
                console.error("Error fetching quotes:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchQuotes();
    }, []);

    const aggTerm = resolveDocTerminology(orgTermDefault); // r1-p3-docterm-pquotes-7e2d9b41 — aggregate labels below

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-green-600" /></div>;

    return (
        <div className="max-w-4xl">
            <div className="flex justify-between items-end mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">{aggTerm.wordPlural}</h1>
                    <p className="text-gray-500 mt-1">Review and approve your {aggTerm.wordPlural.toLowerCase()}</p>
                    {/* r1-p3-docterm-pquotes-7e2d9b41 — aggregate title/subtitle (org default) */}
                </div>
            </div>

            {quotes.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileText className="text-gray-400" size={32} />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">No {aggTerm.wordPlural.toLowerCase()} yet</h3>
                    <p className="text-gray-500 mt-1">When we send you {aggTerm.type === 'estimate' ? 'an' : 'a'} {aggTerm.word.toLowerCase()}, it will appear here.</p>
                    {/* r1-p3-docterm-pquotes-7e2d9b41 — aggregate empty state (org default) */}
                </div>
            ) : (
                <div className="space-y-4">
                    {quotes.map((quote) => (
                        <div 
                            key={quote.id}
                            onClick={() => navigate(`${quote.id}`)}
                            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col md:flex-row md:items-center justify-between hover:shadow-md transition-shadow cursor-pointer group"
                        >
                            <div className="mb-4 md:mb-0">
                                <div className="flex items-center gap-3 mb-1">
                                    <h3 className="text-lg font-bold text-gray-900">
                                        {resolveDocTerminology(quote.docType).word} #{quote.quoteNumber || quote.id.slice(0,6).toUpperCase()}
                                        {/* r1-p3-docterm-pquotes-7e2d9b41 — per-doc row label (freeze) */}
                                    </h3>
                                    <QuoteStatusBadge status={quote.status} />
                                </div>
                                <p className="text-gray-500 text-sm">
                                    Sent: {quote.createdAt?.toDate ? quote.createdAt.toDate().toLocaleDateString() : 'N/A'}
                                </p>
                                {quote.title && (
                                    <p className="text-gray-700 font-medium mt-2">{quote.title}</p>
                                )}
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="text-right">
                                    <p className="text-xs text-gray-500 uppercase font-semibold">Total</p>
                                    <p className="text-xl font-bold text-gray-900">
                                        ${(parseFloat(quote.total) || 0).toFixed(2)}
                                    </p>
                                </div>
                                <ChevronRight className="text-gray-300 group-hover:text-green-600 transition-colors" />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PortalQuotes;